import { type ReactNode, useEffect, useRef, useState } from 'react';
import type { EvolutionSceneSliderId, LandingContent } from '../content';
import { LandingCtaButton } from '../components/LandingCtaButton';
import { Reveal } from '../components/Reveal';
import { SectionContainer } from '../components/SectionContainer';
import type { LandingSectionProps } from '../types';
import { cn } from '../utils';

type EvolutionGridSectionProps = LandingSectionProps & {
  content: LandingContent['evolutionGrid'];
};

const CREATE_BOOK_IMAGE = {
  desktop: '/landing/evolution-create-book-desktop-2.png',
  mobile: '/landing/evolution-create-book-mobile.png',
} as const;

const SCENE_SLIDER_BACKGROUNDS: Record<EvolutionSceneSliderId, string> = {
  weather:
    'linear-gradient(180deg, rgba(208,223,246,0.88) 0%, rgba(198,217,244,0.74) 40%, rgba(190,212,241,0.52) 100%)',
  location:
    'linear-gradient(180deg, rgba(236,227,214,0.9) 0%, rgba(227,214,196,0.78) 42%, rgba(219,205,186,0.56) 100%)',
  time:
    'linear-gradient(180deg, rgba(222,226,236,0.9) 0%, rgba(213,219,232,0.78) 42%, rgba(201,210,226,0.56) 100%)',
};

const SCENE_SLIDER_BACKGROUND_IMAGES: Record<EvolutionSceneSliderId, string> = {
  weather: '/landing/evolution-scene-bg-weather.png',
  location: '/landing/evolution-scene-bg-location.png',
  time: '/landing/evolution-scene-bg-time.png',
};

function SceneSliderIcon({
  type,
  active,
}: {
  type: EvolutionSceneSliderId;
  active: boolean;
}) {
  const stroke = active ? '#FFFFFF' : '#1C1C1E';

  if (type === 'weather') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M4.8 11.4h5.05a2.4 2.4 0 0 0 .45-4.76A3.55 3.55 0 0 0 3.62 7.3 2.1 2.1 0 0 0 4.8 11.4Z"
          stroke={stroke}
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="m8.1 9.55-1.05 2.05h1.2l-.95 1.95"
          stroke={stroke}
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (type === 'location') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M8 13.15s3.1-2.9 3.1-5.75A3.1 3.1 0 1 0 4.9 7.4c0 2.85 3.1 5.75 3.1 5.75Z"
          stroke={stroke}
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="8" cy="7.1" r="1.15" stroke={stroke} strokeWidth="1.25" />
      </svg>
    );
  }

  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="4.6" stroke={stroke} strokeWidth="1.25" />
      <path d="M8 5.55v2.7l1.85 1.1" stroke={stroke} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FeatureCard({
  title,
  description,
  className,
  titleClassName,
  descriptionClassName,
  children,
}: {
  title: string;
  description: string;
  className?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  children?: ReactNode;
}) {
  return (
    <article
      className={cn(
        'relative overflow-hidden rounded-[8px] border border-[#ECEEF4] bg-[#F2F2F7] p-6 text-[#1C1C1E] shadow-[0_18px_54px_rgba(17,18,22,0.06)] sm:p-8 lg:p-12',
        className
      )}
    >
      <div className="relative z-20">
        <h3
          className={cn(
            'max-w-[520px] font-pangaia text-[24px] font-bold leading-[32px] tracking-[-0.032em] sm:text-[36px] sm:leading-[1] lg:text-[40px]',
            titleClassName
          )}
        >
          {title}
        </h3>
        <p
          className={cn(
            'mt-3 max-w-[520px] font-pangram text-[16px] leading-[24px] text-[#636366] sm:mt-4 sm:text-[14px] sm:leading-[22px] lg:text-[15px] lg:leading-[24px]',
            descriptionClassName
          )}
        >
          {description}
        </p>
      </div>
      {children}
    </article>
  );
}

export function EvolutionGridSection({ t, content, tracking }: EvolutionGridSectionProps) {
  const cardsById = Object.fromEntries(content.cards.map((card) => [card.id, card])) as Record<string, (typeof content.cards)[number]>;

  const [sceneSlideIndex, setSceneSlideIndex] = useState(0);
  const [sceneAutoplayPaused, setSceneAutoplayPaused] = useState(false);
  const sceneResumeTimeoutRef = useRef<number | null>(null);

  const [isPhoneLayout, setIsPhoneLayout] = useState(false);

  const sceneSlides = content.sceneSlider.slides;
  const activeSceneSlide = sceneSlides[sceneSlideIndex] ?? sceneSlides[0];

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mobileQuery = window.matchMedia('(max-width: 640px)');
    const apply = () => setIsPhoneLayout(mobileQuery.matches);

    apply();

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', apply);
      return () => mobileQuery.removeEventListener('change', apply);
    }

    mobileQuery.addListener(apply);
    return () => mobileQuery.removeListener(apply);
  }, []);

  useEffect(() => {
    if (sceneAutoplayPaused || sceneSlides.length <= 1) {
      return;
    }

    const interval = window.setInterval(() => {
      setSceneSlideIndex((prev) => (prev + 1) % sceneSlides.length);
    }, 3400);

    return () => window.clearInterval(interval);
  }, [sceneAutoplayPaused, sceneSlides.length]);

  useEffect(() => {
    return () => {
      if (sceneResumeTimeoutRef.current) {
        window.clearTimeout(sceneResumeTimeoutRef.current);
      }
    };
  }, []);

  const setSceneSlide = (index: number, pauseForMs = 7000) => {
    setSceneSlideIndex(index);
    setSceneAutoplayPaused(true);

    if (sceneResumeTimeoutRef.current) {
      window.clearTimeout(sceneResumeTimeoutRef.current);
    }

    sceneResumeTimeoutRef.current = window.setTimeout(() => {
      setSceneAutoplayPaused(false);
    }, pauseForMs);
  };

  const pauseSceneAutoplay = () => {
    setSceneAutoplayPaused(true);

    if (sceneResumeTimeoutRef.current) {
      window.clearTimeout(sceneResumeTimeoutRef.current);
      sceneResumeTimeoutRef.current = null;
    }
  };

  const resumeSceneAutoplay = () => {
    setSceneAutoplayPaused(false);

    if (sceneResumeTimeoutRef.current) {
      window.clearTimeout(sceneResumeTimeoutRef.current);
      sceneResumeTimeoutRef.current = null;
    }
  };

  const activeCreateBookImage = isPhoneLayout ? CREATE_BOOK_IMAGE.mobile : CREATE_BOOK_IMAGE.desktop;

  return (
    <section className="-mt-px bg-white py-20 text-[#1C1C1E] sm:py-24 lg:py-[108px]">
      <SectionContainer className="max-w-[1280px]">
        <Reveal>
          <h2 className="mx-auto max-w-[472px] text-center font-pangaia text-[40px] font-bold leading-[0.98] tracking-[-0.04em] sm:text-[52px] lg:text-[56px]">
            {content.title}
          </h2>
        </Reveal>

        <div className="mt-10 grid gap-2 lg:mt-12 lg:grid-cols-2">
          <Reveal variant="up" className="lg:col-span-2">
            <article className="relative h-[440px] overflow-hidden rounded-[8px] border border-[#ECEEF4] bg-[#F2F2F7] p-6 shadow-[0_18px_54px_rgba(17,18,22,0.06)] sm:h-[520px] sm:p-8 lg:h-[640px] lg:p-12">
              <div
                className="absolute inset-0"
                aria-hidden="true"
                style={{
                  background:
                    'linear-gradient(104deg, rgba(83,155,235,0.94) 0%, rgba(186,219,241,0.72) 18%, rgba(242,242,247,0.7) 47%, rgba(239,221,227,0.75) 65%, rgba(215,226,119,0.88) 100%)',
                }}
              />
              <div
                className="absolute inset-0"
                aria-hidden="true"
                style={{
                  background:
                    'radial-gradient(circle at 18% 86%, rgba(52,88,205,0.78) 0%, rgba(52,88,205,0) 36%), radial-gradient(circle at 74% 80%, rgba(243,214,64,0.36) 0%, rgba(243,214,64,0) 26%)',
                }}
              />

              <div className="relative z-20 max-w-[590px]">
                <h3 className="font-pangaia text-[24px] font-bold leading-[32px] tracking-[-0.032em] sm:text-[36px] sm:leading-[1] lg:text-[40px]">
                  {cardsById['create-book'].title}
                </h3>
                <p className="mt-3 font-pangram text-[16px] font-semibold leading-[24px] text-[#585D66] sm:mt-4 sm:text-[14px] sm:leading-[22px] lg:max-w-[560px] lg:text-[15px] lg:leading-[24px]">
                  {cardsById['create-book'].description}
                </p>
              </div>

              <div className="absolute inset-0 z-10 pointer-events-none">
                <img
                  src={activeCreateBookImage}
                  alt={cardsById['create-book'].title}
                  className={cn(
                    'absolute left-1/2 top-[70%] w-[122%] max-w-[1500px] -translate-x-1/2 -translate-y-1/2 object-contain md:scale-[1.18] lg:scale-[1.24]',
                    isPhoneLayout && 'top-[76%] w-[90%] max-w-[410px] scale-100'
                  )}
                  loading="lazy"
                />
              </div>
            </article>
          </Reveal>

          <Reveal delay={80} variant="left" className="h-full">
            <FeatureCard
              title={cardsById['scene-controls'].title}
              description={cardsById['scene-controls'].description}
              className="h-[576px] min-h-[576px] sm:h-[640px] sm:min-h-[640px] lg:h-[640px] lg:min-h-[640px]"
              titleClassName="text-[24px] leading-[32px] sm:text-[34px] sm:leading-[40px] lg:text-[34px] lg:leading-[38px] lg:whitespace-nowrap"
              descriptionClassName="max-w-[500px] text-[16px] leading-[24px]"
            >
              <div
                className="absolute inset-0 transition-[background] duration-500"
                aria-hidden="true"
                style={{ background: SCENE_SLIDER_BACKGROUNDS[activeSceneSlide.id] }}
              />
              <div
                className="absolute inset-0 bg-cover bg-center transition-opacity duration-500"
                aria-hidden="true"
                style={{
                  backgroundImage: `url(${SCENE_SLIDER_BACKGROUND_IMAGES[activeSceneSlide.id]})`,
                  opacity: activeSceneSlide.id === 'weather' ? 0.52 : activeSceneSlide.id === 'location' ? 0.58 : 0.52,
                }}
              />
              <div
                className="absolute inset-0 transition-opacity duration-500"
                aria-hidden="true"
                style={{
                  background:
                    'radial-gradient(circle at 18% 82%, rgba(94,147,255,0.58) 0%, rgba(94,147,255,0) 34%), radial-gradient(circle at 84% 82%, rgba(255,212,104,0.40) 0%, rgba(255,212,104,0) 31%)',
                  opacity: activeSceneSlide.id === 'weather' ? 0.92 : activeSceneSlide.id === 'location' ? 0.72 : 0.5,
                }}
              />
              <div
                className="pointer-events-none absolute inset-0"
                aria-hidden="true"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(242,242,247,0.92) 0%, rgba(242,242,247,0.66) 28%, rgba(242,242,247,0.22) 54%, rgba(242,242,247,0.05) 100%)',
                  backdropFilter: 'blur(18px)',
                }}
              />

              <div
                className="absolute inset-0 z-20"
                onMouseEnter={pauseSceneAutoplay}
                onMouseLeave={resumeSceneAutoplay}
                onFocus={pauseSceneAutoplay}
                onBlur={resumeSceneAutoplay}
              >
                <div className="absolute left-1/2 top-[58%] w-[min(84%,400px)] -translate-x-1/2 -translate-y-1/2 sm:top-[61%] sm:w-[min(90%,400px)]">
                  <img
                    key={activeSceneSlide.id}
                    src={activeSceneSlide.image}
                    alt={`${activeSceneSlide.badge}: ${activeSceneSlide.body}`}
                    className="landing-bubble-in block w-full max-w-[330px] rounded-[16px] shadow-[0_18px_44px_rgba(17,19,26,0.12)] sm:max-w-none"
                    loading="lazy"
                  />
                </div>

                <div className="absolute bottom-7 left-1/2 flex -translate-x-1/2 items-center justify-center gap-1 sm:bottom-6 lg:bottom-8">
                  {sceneSlides.map((slide, index) => {
                    const isActive = slide.id === activeSceneSlide.id;

                    return (
                      <button
                        key={slide.id}
                        type="button"
                        onClick={() => setSceneSlide(index)}
                        className={cn(
                          'inline-flex h-8 items-center justify-between gap-2 rounded-[80px] border px-3 font-pangram text-[12px] font-semibold leading-[16px] transition-[background-color,color,border-color,box-shadow,transform] duration-300',
                          slide.id === 'time' ? 'w-[72px]' : 'w-[94px]',
                          isActive
                            ? 'border-[#6195FF] bg-[#6195FF] text-white shadow-[0_8px_16px_rgba(97,149,255,0.24)]'
                            : 'border-[rgba(28,28,30,0.16)] bg-white/36 text-[#1C1C1E] hover:bg-white/50'
                        )}
                        aria-pressed={isActive}
                      >
                        <span>{slide.tabLabel}</span>
                        <SceneSliderIcon type={slide.id} active={isActive} />
                      </button>
                    );
                  })}
                </div>
              </div>
            </FeatureCard>
          </Reveal>

          <Reveal delay={120} variant="right" className="h-full">
            <FeatureCard
              title={cardsById['chat-characters'].title}
              description={cardsById['chat-characters'].description}
              className="h-[528px] bg-[linear-gradient(180deg,#F4F4F8_0%,#EFF1F6_100%)] sm:h-[560px] lg:h-[640px]"
              titleClassName="text-[24px] leading-[32px] sm:text-[34px] sm:leading-[40px] lg:text-[34px] lg:leading-[38px] lg:whitespace-nowrap"
              descriptionClassName="text-[16px] leading-[24px]"
            >
              <div className="relative mt-6 flex min-h-[236px] items-end justify-center overflow-hidden sm:mt-10 sm:min-h-[356px] lg:mt-[82px]">
                <div className="absolute left-[18%] top-[28%] h-16 w-16 rounded-full bg-[rgba(255,168,119,0.32)] blur-2xl sm:h-20 sm:w-20" />
                <div className="absolute right-[16%] bottom-[18%] h-16 w-16 rounded-full bg-[rgba(137,225,201,0.3)] blur-2xl sm:h-20 sm:w-20" />
                <video
                  className="relative block h-auto w-full max-w-[352px] rounded-[24px] object-contain"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  poster="/landing/evolution-chat-characters.png"
                  aria-label={cardsById['chat-characters'].title}
                >
                  <source src="/landing/evolution-chat-characters.mp4" type="video/mp4" />
                </video>
              </div>
            </FeatureCard>
          </Reveal>

          <Reveal delay={160} variant="left" className="h-full">
            <FeatureCard
              title={cardsById['relationships'].title}
              description={cardsById['relationships'].description}
              className="h-[528px] bg-[#F2F2F7] sm:h-[560px] lg:h-[640px]"
            >
              <div className="relative mt-8 flex justify-center sm:mt-10 lg:mt-[78px]">
                <video
                  className="block h-auto w-full max-w-[418px] object-contain"
                  autoPlay
                  muted
                  playsInline
                  preload="metadata"
                  poster="/hero/slide-2-desktopV2.png"
                >
                  <source src="/landing/relationships.mp4" type="video/mp4" />
                </video>
              </div>
            </FeatureCard>
          </Reveal>

          <Reveal delay={200} variant="right" className="h-full">
            <FeatureCard
              title={cardsById['share'].title}
              description={cardsById['share'].description}
              className="h-[528px] min-h-[528px] overflow-hidden bg-[#E7ECFF] sm:h-[592px] sm:min-h-[592px] lg:h-[640px] lg:min-h-[640px]"
              titleClassName="text-[24px] leading-[32px] sm:text-[34px] sm:leading-[40px] lg:text-[40px] lg:leading-[40px]"
              descriptionClassName="text-[16px] leading-[24px]"
            >
              <div
                className="absolute inset-0 bg-cover bg-[center_42%] bg-no-repeat sm:bg-center"
                aria-hidden="true"
                style={{ backgroundImage: 'url(/landing/evolution-share-bg.png)' }}
              />
              <img
                src="/landing/evolution-shareV2.png"
                alt=""
                className="absolute bottom-[-28px] left-1/2 w-[136%] max-w-none -translate-x-1/2 object-contain sm:bottom-0 sm:w-[106%] sm:max-w-[560px] lg:w-[102%] lg:max-w-[700px]"
                loading="lazy"
                aria-hidden="true"
              />
              <div
                className="pointer-events-none absolute inset-0"
                aria-hidden="true"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(236,239,255,0.94) 0%, rgba(236,239,255,0.78) 20%, rgba(236,239,255,0.18) 44%, rgba(236,239,255,0.03) 100%)',
                }}
              />
            </FeatureCard>
          </Reveal>
        </div>

        <Reveal delay={240} variant="up" className="mt-2">
          <div className="flex flex-col gap-4 rounded-[8px] bg-[#0F131D] px-5 py-4 text-white shadow-[0_22px_60px_rgba(17,19,26,0.18)] sm:px-6 lg:min-h-[80px] lg:flex-row lg:items-center lg:justify-between">
            <p className="font-pangram text-[24px] font-semibold leading-[1.02] sm:text-[28px]">{content.ribbonTitle}</p>
            <p className="bg-[linear-gradient(90deg,#6195FF_0%,#E6EEFF_100%)] bg-clip-text font-pangaia text-[20px] font-bold leading-[28px] text-transparent sm:text-[24px] sm:leading-[32px] lg:mr-auto lg:pl-6">
              {content.ribbonSubtitle}
            </p>
            <LandingCtaButton
              label={t.getStarted || 'Get started'}
              placement="ribbon_cta"
              tracking={tracking}
              size="md"
              className="self-start lg:self-auto"
            />
          </div>
        </Reveal>
      </SectionContainer>
    </section>
  );
}
